import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Card,
  Divider
} from '@mui/material';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { GlassEmptyState } from 'shared/ui/GlassEmptyState';
import { selectedCFDState } from 'features/cfd/state/cfdAtoms';
import { errorCodesState } from 'features/error-codes/state/errorCodesAtoms';
import { lookupErrorMessage } from 'features/error-codes/utils/lookupErrorMessage';
import { OBJECTIVE_PRESETS } from 'shared/domain/objectivePresets';
import { getCFDObjectiveStatusLabel, getCFDStatusLabel } from 'features/cfd/utils/cfdStatus';
import { ObjectiveStatusCard } from './ObjectiveStatusCard';

const subtitleSx = {
  display: 'flex',
  alignItems: 'center',
};

const cardSx = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minHeight: 28,
  paddingX: 1,
  paddingY: 0.5,
  width: '100%'
};

export const CFDStatus: React.FC = () => {
  const selectedCFD = useRecoilValue(selectedCFDState);
  const errorCodesDict = useRecoilValue(errorCodesState);

  if (!selectedCFD) {
    return (
      <Box component="div" sx={{ boxSizing: 'border-box', flexGrow: 1, paddingTop: 1 }}>
        <Box component="div" sx={subtitleSx}>
          <CustomTypography>CFD Status</CustomTypography>
        </Box>
        <Divider sx={{ my: 0.5, bgcolor: '#e1e1e1' }} />
        <GlassEmptyState message="No CFD selected." />
      </Box>
    );
  }

  const preset = OBJECTIVE_PRESETS[selectedCFD.presetId];
  const errorLookup = lookupErrorMessage(selectedCFD.error_code ?? null, errorCodesDict);
  const errorReason = errorLookup
    ? errorLookup.message ?? `不明なエラー (${errorLookup.code})`
    : null;

  return (
    <Box component="div" sx={{ boxSizing: 'border-box', flexGrow: 1, paddingTop: 1 }}>
      <Box component="div" sx={subtitleSx}>
        <CustomTypography>CFD Status</CustomTypography>
      </Box>

      <Divider sx={{ my: 0.5, bgcolor: '#e1e1e1' }} />

      <Box component="div" sx={{ paddingBottom: 0.5 }}>
        <Card sx={{ backgroundColor: '#e3f2fd', ...cardSx }}>
          <CustomTypography sx={{ color: '#0d47a1' }}>
            Study Status: {getCFDStatusLabel(selectedCFD.status_code)}
          </CustomTypography>
        </Card>
      </Box>

      {errorReason && (
        <Box component="div" sx={{ paddingBottom: 0.5 }}>
          <Card sx={{ backgroundColor: '#ffebee', ...cardSx }}>
            <CustomTypography sx={{ color: '#b71c1c' }}>
              失敗理由: {errorReason}
            </CustomTypography>
          </Card>
        </Box>
      )}

      <Box
        component="div"
        sx={{
          display: 'grid',
          gap: 0.75,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(340px, 1fr))' },
          paddingBottom: 0.5,
        }}
      >
        {preset.objectives.map((objective) => (
          <ObjectiveStatusCard
            key={objective.key}
            objective={objective}
            cfdId={selectedCFD.cfd_id?.[objective.key]}
            value={selectedCFD.value?.[objective.key]}
            statusLabel={getCFDObjectiveStatusLabel(selectedCFD, objective.key)}
          />
        ))}
      </Box>
    </Box>
  );
};
